import { OrgToolsTable } from '@/components/OrgToolsTable/OrgToolsTable'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog'
import {
  flattenToolPages,
  useDeleteTool,
  useListToolsQuery,
  useUpdateTool,
} from '@/services/queries/tools'
import { useMemo } from 'react'

type OrgToolsModalProps = {
  open: boolean
  onClose: () => void
  toolId?: string
  ariaLabel?: string
  isReadOnly?: boolean
}

export const OrgToolsModal = ({
  open,
  onClose,
  toolId,
  ariaLabel = 'Tool modal',
  isReadOnly = false,
}: OrgToolsModalProps) => {
  const { data, isLoading, fetchNextPage } = useListToolsQuery({
    limit: 100,
    offset: 0,
    enabled: open,
  })
  const tools = useMemo(() => {
    const flattenedTools = flattenToolPages(data)

    if (!toolId) {
      return flattenedTools
    }

    return flattenedTools.filter((tool) => tool.id === toolId)
  }, [data, toolId])
  const totalTools = tools.length
  const { mutate: deleteTool } = useDeleteTool()
  const { mutate: updateTool } = useUpdateTool()

  return (
    <Dialog
      open={open}
      onOpenChange={(nextOpen) => {
        if (!nextOpen) {
          onClose()
        }
      }}
    >
      <DialogContent
        ariaLabel={ariaLabel}
        className="w-[92vw] max-w-4xl min-h-0 max-h-[82vh] overflow-hidden rounded-2xl border-zinc-200 bg-zinc-50 p-0 gap-0"
      >
        <DialogHeader className="px-6 py-6 border-b border-zinc-200">
          <DialogTitle className="text-xl leading-none font-semibold tracking-[-0.02em] text-zinc-900">
            Edit Tools
          </DialogTitle>
          <DialogDescription className="text-zinc-500 text-sm">
            Double click a tool to edit its name.
          </DialogDescription>
        </DialogHeader>
        <div className="p-6 overflow-auto">
          <OrgToolsTable
            tools={tools}
            totalTools={totalTools}
            isLoading={isLoading}
            fetchNextPage={() => {
              void fetchNextPage()
            }}
            onUpdate={updateTool}
            onDelete={deleteTool}
            isReadOnly={isReadOnly}
          />
        </div>
        <div className="flex justify-center px-6 pb-6 bg-zinc-50">
          <a
            href="/tools"
            target="__blank"
            className="inline-flex h-9 items-center justify-center whitespace-nowrap rounded-full border border-input bg-background px-4 py-2 text-sm font-medium shadow-sm transition-colors hover:bg-accent hover:text-accent-foreground"
          >
            View Tools Library
          </a>
        </div>
      </DialogContent>
    </Dialog>
  )
}
